// Pure validation/normalization for the top-up amount, exported so it can be
// unit-tested directly — it's the guard against every "stupid input" a user can
// put in the custom-amount field (empty, letters, negatives, sub-cent, absurdly
// large, Infinity, sub-cent rounding drift).
//
// Bounds mirror the server: valoi-billing's MIN_TOPUP_CENTS is 50 ($0.50). Billing
// has NO max configured (MAX_TOPUP_CENTS unset = no cap), so the ceiling here is a
// purely client-side sanity guard to stop a fat-fingered charge from ever leaving
// the browser — the server would otherwise happily attempt it.
import { formatUsd } from '@/shared/utils/format';

export const MIN_TOPUP_USD = 0.5;
export const MAX_TOPUP_USD = 5000;

export type TopUpAmountResult =
  | { ok: true; amountUsd: number; amountCents: number }
  | { ok: false; message: string };

export function parseTopUpAmount(raw: string | number): TopUpAmountResult {
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());

  // Number('') is 0 and Number('12x') is NaN — both handled below.
  if (!Number.isFinite(n)) {
    return { ok: false, message: 'Enter a valid dollar amount.' };
  }
  if (n <= 0) {
    return { ok: false, message: 'Enter an amount greater than $0.' };
  }

  // Charge in whole cents; this also collapses sub-cent input (e.g. 10.999 -> 1100)
  // so the amount we show, send, and get charged all agree.
  const amountCents = Math.round(n * 100);

  if (amountCents < MIN_TOPUP_USD * 100) {
    return { ok: false, message: `The minimum top-up is ${formatUsd(MIN_TOPUP_USD)}.` };
  }
  if (amountCents > MAX_TOPUP_USD * 100) {
    return { ok: false, message: `The maximum top-up is ${formatUsd(MAX_TOPUP_USD)}.` };
  }

  return { ok: true, amountUsd: amountCents / 100, amountCents };
}
